import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import {NexusSearchSchemaEntryNames} from "../../../../../../../../core/constants/enums";
import axios from "axios";
import {Box, Button, Typography} from "@mui/material";
import LoadingLottieAnimation from "../../../../../../../components/app-specific/lottie-animations/loading";
import Grid from "@mui/material/Unstable_Grid2";
import LookupProductCard from "../../../../../../../components/app-specific/lookup-product-card";

const ProductsOtherIndustriesContainer = ({
                                              industries,
                                              searchData,
                                              setSearchData,
                                              searchSchema,
                                              isAdvancedMode,
                                              performNexusSearch,
                                              submitProductSelection,
                                              getProductNavigationLink,
                                              setOtherIndustriesHaveResults,
                                              headerId,
                                          }) => {
    const [loading, setLoading] = useState(false);
    const [resultsOfOtherIndustries, setResultsOfOtherIndustries] = useState({});
    const isMounted = useIsMounted();

    const otherIndustries = useMemo(() => industries.filter(e => e.id !== searchData.industryId), [industries, searchData.industryId]);

    const searchRequestData = useMemo(() => {
        const commonEntries = [
            searchSchema.entries.find(e => e.name === NexusSearchSchemaEntryNames.parentCategory),
            searchSchema.entries.find(e => e.name === NexusSearchSchemaEntryNames.category),
            searchSchema.entries.find(e => e.name === NexusSearchSchemaEntryNames.partNo),
            searchSchema.entries.find(e => e.name === NexusSearchSchemaEntryNames.tag),
            searchSchema.entries.find(e => e.name === NexusSearchSchemaEntryNames.code),
        ].filter(e => !!e);
        const filters = [];
        for (const selectedFilter of searchData.filters) {
            if (commonEntries.some(e => e.filterId === selectedFilter.filterId)) {
                filters.push(selectedFilter);
            }
        }
        return {
            isAdvancedMode: isAdvancedMode,
            keyword: searchData.keyword?.trim()?.length ? searchData.keyword?.trim() : null,
            pagination: {pageSize: 3, currentPage: 1},
            sortBy: null,
            filters: filters.map(e => ({
                industryIndexStructureId: e.filterId,
                value: e.value?.toString() ?? null,
            })),
        }
    }, [searchSchema.entries, isAdvancedMode, searchData.keyword, searchData.filters])

    /**
     * Resets the results of other industries when the search data changes.
     */
    useEffect(() => {
        setResultsOfOtherIndustries({});
    }, [searchRequestData.isAdvancedMode, searchRequestData.keyword, searchRequestData.filters]);

    /**
     * Fetches the products based on the given search parameters.
     * @type {(function(*, *): Promise<void>)}
     */
    const fetchResultsForIndustry = useCallback(async (industry, cancelTokenSource) => {
        const forApi = {
            ...searchRequestData,
            industryId: industry.id,
        }
        const res = await performNexusSearch(forApi, cancelTokenSource.token);
        if (!res || !isMounted())
            return;
        const [productsList, , , searchToken] = res;
        if (productsList.length === 0)
            return;
        setResultsOfOtherIndustries(prevState => ({
            ...prevState,
            [industry.id]: {
                productsList: productsList,
                searchToken: searchToken,
                searchData: {
                    industryId: industry.id,
                    keyword: searchRequestData.keyword,
                    filters: searchRequestData.filters,
                },
            },
        }))
    }, [isMounted, performNexusSearch, searchRequestData])

    /**
     * Fetches the products based on the given search parameters.
     * - This is necessary because the search data is industry-specific.
     */
    useEffect(() => {
        if (!otherIndustries.length)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        const promises = [];
        setLoading(true);
        for (const industry of otherIndustries) {
            promises.push(fetchResultsForIndustry(industry, cancelTokenSource));
        }
        Promise.all(promises).then(() => {
            if (!cancelTokenSource.token.reason && isMounted()) {
                setLoading(false);
            }
        })
        return () => cancelTokenSource.cancel();
    }, [fetchResultsForIndustry, otherIndustries, isMounted])

    /**
     * Checks if the other industries have results.
     */
    useEffect(() => {
        setOtherIndustriesHaveResults(Object.values(resultsOfOtherIndustries).length > 0);
    }, [resultsOfOtherIndustries, setOtherIndustriesHaveResults]);

    return (
        <>
            {
                loading &&
                <Box
                    sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', marginBlock: 6}}>
                    <LoadingLottieAnimation width={'80%'}/>
                </Box>
            }
            {
                Object.values(resultsOfOtherIndustries).length > 0 &&
                <>
                    <Typography
                        variant={'subtitle1'}
                        marginBlockStart={4}
                        marginBlockEnd={2}
                        fontWeight={600}
                        id={headerId}
                    >
                        Products from Other Industries
                    </Typography>
                    {
                        Object.values(resultsOfOtherIndustries).map((results) => {
                            const industry = industries.find(e => e.id === results.searchData.industryId);
                            return (
                                <Box key={industry.id} sx={{display: 'flex', flexDirection: 'column', gap: 2, marginBlock: 3}}>
                                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                        <Typography
                                            variant={'subtitle2'}
                                            marginBlockStart={2}
                                            marginBlockEnd={1}
                                            fontWeight={600}
                                            color={'tertiary.main'}
                                        >
                                            {industry.title}
                                        </Typography>
                                        <Button
                                            variant={'text'}
                                            color={'primary'}
                                            onClick={() => setSearchData(results.searchData)}
                                        >
                                            View All
                                        </Button>
                                    </Box>
                                    <Grid container spacing={3} margin={0}>
                                        {
                                            results?.productsList?.map((product) => (
                                                <Grid key={product?.id} xs={12} sm={6} md={12} mdlg={6} lgxl={4} xxl={3}>
                                                    <LookupProductCard
                                                        info={product}
                                                        link={{href: getProductNavigationLink(product.partNo, product.codes, product.id, results.searchToken), replace: false}}
                                                        onProductClicked={(e) => submitProductSelection(e, product.info, results.searchToken)}
                                                    />
                                                </Grid>
                                            ))
                                        }
                                    </Grid>
                                </Box>
                            )
                        })
                    }
                </>
            }
        </>
    );
}

export default ProductsOtherIndustriesContainer;